/**
 * ScorecardItemCard — Inline-editable scorecard item for the full-page editor.
 *
 * Features:
 * - Click-to-edit title and description (no pencil icon needed)
 * - Importance slider (Nice / Important / Critical) always visible
 * - Draggable threshold range bar for numeric specs
 * - Categorical single/multi-select for categorical specs
 * - Boolean toggle for boolean specs
 * - Spec linkage display
 */
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Trash2, GripVertical, Repeat } from 'lucide-react'
import { api } from '../../services/api'
import { polarityLabel, polarityZones, ZoneRole } from '../../lib/specPolarity'

// ── Types ──────────────────────────────────────────────────────────

export interface OrderedLevel {
  id: number
  value: string
  label: string
  description?: string
  order: number
}

export interface CategoryOptionData {
  id: number
  value: string
  label: string
  description?: string
  order: number
}

export interface ScorecardItemData {
  id: number
  title: string
  description: string
  importance: 'nice_to_have' | 'important' | 'mission_critical'
  order: number
  spec?: number | null
  spec_name?: string
  spec_type?: string  // 'numeric' | 'boolean' | 'categorical' | 'ordered_categorical' | 'text'
  spec_unit?: string
  spec_typical_min?: string | number | null  // CharField from backend
  spec_typical_max?: string | number | null  // CharField from backend
  spec_categories?: any[]  // category_options JSON from Spec
  lower_threshold?: number | null
  upper_threshold?: number | null
  higher_is_better?: boolean  // read-only, comes from Spec
  acceptable_categories?: string[]
  required_boolean_value?: boolean | null
  requires_llm_scoring: boolean
  // Ordered categorical fields
  spec_ordered_levels?: OrderedLevel[]
  minimum_acceptable_level?: number | null  // FK id
  desirable_level?: number | null  // FK id
  minimum_acceptable_level_detail?: OrderedLevel | null
  desirable_level_detail?: OrderedLevel | null
  // Categorical option fields
  spec_options?: CategoryOptionData[]
}

interface ScorecardItemCardProps {
  item: ScorecardItemData
  mode: 'edit' | 'score' | 'filter'
  onUpdate?: (itemId: number, updates: Partial<ScorecardItemData>) => void
  onDelete?: (itemId: number) => void
  onLinkSpec?: (itemId: number) => void
  scoreValue?: number
  scoreLabel?: string
}

// Soft token tint via color-mix — the design tokens carry no alpha channel, so
// Tailwind's `/opacity` modifier renders them transparent; color-mix keeps these
// themeable (and dark-mode-correct) instead of using raw palette colors.
const tint = (varName: string, pct: number): React.CSSProperties => ({
  backgroundColor: `color-mix(in srgb, var(${varName}) ${pct}%, transparent)`,
})

// ── Importance helpers ─────────────────────────────────────────────

type ImportanceLevel = 'nice_to_have' | 'important' | 'mission_critical'

const importanceOptions: {
  value: ImportanceLevel
  label: string
  abbr: string
  text: string
  bgVar: string
  desc: string
}[] = [
  { value: 'nice_to_have', label: 'APA — Nice to Have', abbr: 'APA', text: 'text-muted-foreground', bgVar: '--muted-foreground', desc: 'Additional Performance Attribute' },
  { value: 'important', label: 'KSA — Important', abbr: 'KSA', text: 'text-primary', bgVar: '--primary', desc: 'Key System Attribute' },
  { value: 'mission_critical', label: 'KPP — Critical', abbr: 'KPP', text: 'text-destructive', bgVar: '--destructive', desc: 'Key Performance Parameter' },
]

// ── Threshold Range Bar ────────────────────────────────────────────

// Design tokens for each zone role. The roles themselves (which side is the
// failing end) come from `polarityZones` — the same contract NumericCriteriaSlider
// renders with its own palette.
const ZONE_VAR: Record<ZoneRole, string> = {
  fail: '--destructive',
  acceptable: '--warning',
  desirable: '--success',
}

const ZONE_TEXT: Record<ZoneRole, string> = {
  fail: 'text-destructive',
  acceptable: 'text-warning',
  desirable: 'text-success',
}

const ThresholdRangeBar: React.FC<{
  min: number
  max: number
  unit: string
  lowerThreshold: number
  upperThreshold: number
  /** When false the zone colours flip: left = desirable, right = failure. */
  higherIsBetter?: boolean
  onChangeLower: (v: number) => void
  onChangeUpper: (v: number) => void
  /** Rendered to the right of the bar (the polarity toggle). */
  action?: React.ReactNode
}> = ({
  min: rawMin,
  max: rawMax,
  unit,
  lowerThreshold: rawLower,
  upperThreshold: rawUpper,
  higherIsBetter = true,
  onChangeLower,
  onChangeUpper,
  action,
}) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<'lower' | 'upper' | null>(null)

  // Coerce everything to number — Django DecimalField serializes as strings
  const min = Number(rawMin) || 0
  const max = Number(rawMax) || 100
  const lowerThreshold = Number(rawLower) || min
  const upperThreshold = Number(rawUpper) || max

  const range = max - min || 1
  const valueToPercent = (v: number) => ((v - min) / range) * 100
  const percentToValue = (p: number) => min + (p / 100) * range

  const formatValue = (v: number | string) => {
    const n = Number(v)
    if (isNaN(n)) return String(v)
    if (Math.abs(n) >= 100) return Math.round(n).toString()
    return n.toFixed(1)
  }

  const getPositionPercent = useCallback(
    (clientX: number) => {
      if (!trackRef.current) return 0
      const rect = trackRef.current.getBoundingClientRect()
      return Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100))
    },
    []
  )

  const handleMove = useCallback(
    (clientX: number) => {
      if (!dragging) return
      const percent = getPositionPercent(clientX)
      const value = percentToValue(percent)

      if (dragging === 'lower') {
        const clamped = Math.min(value, upperThreshold - range * 0.05)
        onChangeLower(Math.round(clamped * 10) / 10)
      } else {
        const clamped = Math.max(value, lowerThreshold + range * 0.05)
        onChangeUpper(Math.round(clamped * 10) / 10)
      }
    },
    [dragging, getPositionPercent, percentToValue, lowerThreshold, upperThreshold, range, onChangeLower, onChangeUpper]
  )

  useEffect(() => {
    if (!dragging) return
    const onMouseMove = (e: MouseEvent) => handleMove(e.clientX)
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) handleMove(e.touches[0].clientX)
    }
    const onEnd = () => setDragging(null)

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onEnd)
    document.addEventListener('touchmove', onTouchMove)
    document.addEventListener('touchend', onEnd)
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onEnd)
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('touchend', onEnd)
    }
  }, [dragging, handleMove])

  // lower_threshold stays physically left in both directions — only the zone
  // meanings flip. Swapping the values here would break scoring (DEV-792).
  const lPct = valueToPercent(lowerThreshold)
  const uPct = valueToPercent(upperThreshold)
  const zones = polarityZones(higherIsBetter)

  return (
    <div className="flex items-center gap-1.5 py-1">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>
            {formatValue(min)}
            {unit && ` ${unit}`}
          </span>
          <span>
            {formatValue(max)}
            {unit && ` ${unit}`}
          </span>
        </div>

        <div ref={trackRef} className="relative h-2.5 rounded-full overflow-hidden cursor-pointer select-none">
          {/* Zones — roles flip with polarity, boundaries never move */}
          <div
            data-testid="threshold-zone-left"
            data-zone-role={zones.left}
            className="absolute top-0 bottom-0 left-0"
            style={{ width: `${lPct}%`, ...tint(ZONE_VAR[zones.left], 55) }}
          />
          <div
            data-testid="threshold-zone-middle"
            data-zone-role={zones.middle}
            className="absolute top-0 bottom-0"
            style={{ left: `${lPct}%`, width: `${uPct - lPct}%`, ...tint(ZONE_VAR[zones.middle], 55) }}
          />
          <div
            data-testid="threshold-zone-right"
            data-zone-role={zones.right}
            className="absolute top-0 bottom-0 right-0"
            style={{ left: `${uPct}%`, ...tint(ZONE_VAR[zones.right], 55) }}
          />

          {/* Handles — each takes the colour of the zone it borders */}
          {(['lower', 'upper'] as const).map((which) => {
            const pct = which === 'lower' ? lPct : uPct
            const handleVar = ZONE_VAR[which === 'lower' ? zones.left : zones.right]
            return (
              <div
                key={which}
                data-testid={`threshold-handle-${which}`}
                className="absolute top-0 bottom-0 w-4 cursor-ew-resize flex items-center justify-center z-10"
                style={{ left: `calc(${pct}% - 8px)` }}
                onMouseDown={(e) => {
                  e.preventDefault()
                  setDragging(which)
                }}
                onTouchStart={(e) => {
                  e.preventDefault()
                  setDragging(which)
                }}
              >
                <div
                  className={`h-full rounded-full transition-all ${dragging === which ? 'w-1' : 'w-0.5 hover:w-1'}`}
                  style={{ backgroundColor: `var(${handleVar})` }}
                />
              </div>
            )
          })}
        </div>

        {/* Labels */}
        <div className="relative h-4">
          <span
            data-testid="threshold-label-lower"
            className={`absolute text-[10px] tabular-nums transition-all ${ZONE_TEXT[zones.left]} ${
              dragging === 'lower' ? 'font-semibold scale-110' : ''
            }`}
            style={{ left: `${lPct}%`, transform: 'translateX(-50%)' }}
          >
            {formatValue(lowerThreshold)}
          </span>
          <span
            data-testid="threshold-label-upper"
            className={`absolute text-[10px] tabular-nums transition-all ${ZONE_TEXT[zones.right]} ${
              dragging === 'upper' ? 'font-semibold scale-110' : ''
            }`}
            style={{ left: `${uPct}%`, transform: 'translateX(-50%)' }}
          >
            {formatValue(upperThreshold)}
          </span>
        </div>
      </div>

      {action}
    </div>
  )
}

// ── Polarity toggle ───────────────────────────────────────────────

/**
 * Flips `Spec.higher_is_better`. The flag lives on the shared Spec (Jon's
 * option-A ruling, 2026-09-20), so one click moves every program using that
 * spec — the tooltip says so, because the blast radius has to be legible from
 * the per-program screen the control sits on.
 */
const PolarityToggle: React.FC<{
  specName: string
  higherIsBetter: boolean
  pending: boolean
  error: string | null
  onToggle: () => void
}> = ({ specName, higherIsBetter, pending, error, onToggle }) => {
  const spec = specName || 'this spec'
  const tooltip = error
    ? `Couldn't change polarity: ${error}. Click to retry.`
    : [
        `${polarityLabel(higherIsBetter)} — click to flip the colour scale.`,
        `Polarity is stored on the spec "${spec}", so this applies everywhere that spec is used, not just this program.`,
      ].join('\n')

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={pending}
      title={tooltip}
      aria-label={tooltip}
      aria-pressed={!higherIsBetter}
      className={`flex-shrink-0 rounded p-1 transition-colors disabled:opacity-40 ${
        error
          ? 'text-destructive hover:bg-destructive/10'
          : 'text-muted-foreground/40 hover:bg-muted/60 hover:text-foreground'
      }`}
    >
      <Repeat className="h-3 w-3" />
    </button>
  )
}

// ── Categorical Selector ───────────────────────────────────────────

interface CategoryOption {
  value: string
  label?: string
  description?: string
}

const CategoricalSelector: React.FC<{
  categories: (string | CategoryOption)[]
  selected: string[]
  onChange: (selected: string[]) => void
  multi?: boolean
}> = ({ categories, selected, onChange, multi = true }) => {
  const normalize = (cat: string | CategoryOption): { value: string; label: string } => {
    if (typeof cat === 'string') return { value: cat, label: cat }
    return { value: cat.value, label: cat.label || cat.value }
  }

  const toggle = (val: string) => {
    if (multi) {
      if (selected.includes(val)) {
        onChange(selected.filter((c) => c !== val))
      } else {
        onChange([...selected, val])
      }
    } else {
      onChange(selected.includes(val) ? [] : [val])
    }
  }

  return (
    <div className="flex flex-wrap gap-1 py-1">
      {categories.map((cat) => {
        const { value, label } = normalize(cat)
        const isSelected = selected.includes(value)
        return (
          <button
            key={value}
            onClick={() => toggle(value)}
            className={`px-2 py-0.5 text-[10px] rounded-full border transition-colors ${
              isSelected
                ? 'border-primary text-primary font-medium'
                : 'border-border text-muted-foreground hover:border-muted-foreground'
            }`}
            style={isSelected ? tint('--primary', 12) : undefined}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}

// ── Ordered Categorical Bar ───────────────────────────────────────

const OrderedCategoricalBar: React.FC<{
  levels: OrderedLevel[]
  minimumAcceptableLevelId: number | null
  desirableLevelId: number | null
  higherIsBetter?: boolean
  onChangeMinAcceptable: (id: number | null) => void
  onChangeDesirable: (id: number | null) => void
  /** Rendered to the right of the bar (the polarity toggle). */
  action?: React.ReactNode
}> = ({ levels, minimumAcceptableLevelId, desirableLevelId, higherIsBetter = true, onChangeMinAcceptable, onChangeDesirable, action }) => {
  const sorted = [...levels].sort((a, b) => a.order - b.order)
  const minIdx = sorted.findIndex((l) => l.id === minimumAcceptableLevelId)
  const desIdx = sorted.findIndex((l) => l.id === desirableLevelId)

  // Returns the status token for a level's zone (fail / acceptable / desirable),
  // or null for the neutral (unset) zone.
  const getZoneVar = (idx: number): string | null => {
    if (higherIsBetter) {
      // [fail] min [acceptable] des [desirable]
      if (minIdx >= 0 && idx < minIdx) return '--destructive'
      if (desIdx >= 0 && idx >= desIdx) return '--success'
      if (minIdx >= 0 && desIdx >= 0 && idx >= minIdx && idx < desIdx) return '--warning'
    } else {
      // [desirable] des [acceptable] min [fail]
      if (minIdx >= 0 && idx > minIdx) return '--destructive'
      if (desIdx >= 0 && idx <= desIdx) return '--success'
      if (minIdx >= 0 && desIdx >= 0 && idx > desIdx && idx <= minIdx) return '--warning'
    }
    return null
  }

  const zoneStyle = (idx: number): React.CSSProperties | undefined => {
    const v = getZoneVar(idx)
    if (!v) return undefined
    return {
      backgroundColor: `color-mix(in srgb, var(${v}) 20%, transparent)`,
      borderColor: `color-mix(in srgb, var(${v}) 40%, transparent)`,
    }
  }

  return (
    <div className="py-1 space-y-1">
      <div className="flex items-center gap-0.5">
        {sorted.map((level, idx) => (
          <div
            key={level.id}
            className="flex-1 text-center px-0.5 py-1 rounded border border-border text-[9px] leading-tight transition-colors"
            style={zoneStyle(idx)}
            title={level.label}
          >
            <div className="font-medium truncate">{level.value}</div>
          </div>
        ))}
        {action}
      </div>
      <div className="flex gap-2 text-[9px] text-muted-foreground">
        <label className="flex items-center gap-1">
          <span>Min OK:</span>
          <select
            className="bg-background border rounded px-1 py-0.5 text-[9px]"
            value={minimumAcceptableLevelId ?? ''}
            onChange={(e) => onChangeMinAcceptable(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">—</option>
            {sorted.map((l) => (
              <option key={l.id} value={l.id}>{l.value}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1">
          <span>Desirable:</span>
          <select
            className="bg-background border rounded px-1 py-0.5 text-[9px]"
            value={desirableLevelId ?? ''}
            onChange={(e) => onChangeDesirable(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">—</option>
            {sorted.map((l) => (
              <option key={l.id} value={l.id}>{l.value}</option>
            ))}
          </select>
        </label>
      </div>
    </div>
  )
}

// ── Inline Text Field ──────────────────────────────────────────────

const InlineTextField: React.FC<{
  value: string
  onChange: (v: string) => void
  placeholder?: string
  className?: string
  multiline?: boolean
}> = ({ value, onChange, placeholder, className = '', multiline }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  useEffect(() => {
    setDraft(value)
  }, [value])

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const commit = () => {
    setEditing(false)
    if (draft !== value) {
      onChange(draft)
    }
  }

  const cancel = () => {
    setDraft(value)
    setEditing(false)
  }

  if (!editing) {
    return (
      <span
        onClick={() => setEditing(true)}
        className={`cursor-text hover:bg-muted/50 rounded px-0.5 -mx-0.5 transition-colors ${className} ${
          !value ? 'text-muted-foreground/50 italic' : ''
        }`}
        title="Click to edit"
      >
        {value || placeholder || 'Click to edit...'}
      </span>
    )
  }

  const sharedProps = {
    value: draft,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        commit()
      }
      if (e.key === 'Escape') cancel()
    },
    onBlur: commit,
    placeholder,
    className: `w-full bg-transparent border-b border-primary/50 outline-none ${className}`,
  }

  if (multiline) {
    return (
      <textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        {...sharedProps}
        onChange={(e) => setDraft(e.target.value)}
        rows={2}
        className={`${sharedProps.className} resize-none`}
      />
    )
  }

  return (
    <input
      ref={inputRef as React.RefObject<HTMLInputElement>}
      type="text"
      {...sharedProps}
      onChange={(e) => setDraft(e.target.value)}
    />
  )
}

// ── Importance Dropdown ────────────────────────────────────────────

const ImportanceDropdown: React.FC<{
  value: ImportanceLevel
  onChange: (v: ImportanceLevel) => void
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const current = importanceOptions.find((o) => o.value === value)

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`text-[11px] font-medium rounded-md px-2 py-0.5 cursor-pointer
          transition-colors ${current?.text || ''}`}
        style={current ? tint(current.bgVar, 12) : undefined}
      >
        {current?.label}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-52 rounded-lg border border-border bg-popover shadow-lg overflow-hidden">
          {importanceOptions.map((opt) => {
            const isSelected = opt.value === value
            return (
              <button
                key={opt.value}
                onClick={() => {
                  onChange(opt.value)
                  setOpen(false)
                }}
                className={`w-full text-left px-3 py-2 flex items-center justify-between transition-colors
                  ${isSelected ? 'bg-accent' : 'hover:bg-muted/50'}`}
              >
                <div>
                  <div className={`text-xs font-medium ${isSelected ? 'text-foreground' : 'text-foreground/80'}`}>
                    {opt.label}
                  </div>
                  <div className="text-[10px] text-muted-foreground">{opt.desc}</div>
                </div>
                {isSelected && (
                  <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Main Component ─────────────────────────────────────────────────

const ScorecardItemCard: React.FC<ScorecardItemCardProps> = ({
  item,
  mode,
  onUpdate,
  onDelete,
  scoreValue,
  scoreLabel,
}) => {
  const isEdit = mode === 'edit'

  // ── Spec polarity ──────────────────────────────────────────────
  // `higher_is_better` is read-only on the item payload; it belongs to the
  // shared Spec and is written through PATCH /api/specs/<id>/. We hold an
  // optimistic override locally and drop it whenever the server value arrives,
  // so the card never needs a second write path through its parent.
  const specId = item.spec ?? null
  const serverPolarity = item.higher_is_better ?? true
  const [polarityOverride, setPolarityOverride] = useState<boolean | null>(null)
  const [polarityPending, setPolarityPending] = useState(false)
  const [polarityError, setPolarityError] = useState<string | null>(null)
  const higherIsBetter = polarityOverride ?? serverPolarity

  useEffect(() => {
    // A refetch is the source of truth again — stop shadowing it.
    setPolarityOverride(null)
    setPolarityError(null)
  }, [serverPolarity])

  const toggleHigherIsBetter = useCallback(async () => {
    if (specId == null) return
    const next = !(polarityOverride ?? serverPolarity)
    setPolarityPending(true)
    setPolarityError(null)
    setPolarityOverride(next)
    try {
      await api.patch(`/api/specs/${specId}/`, { higher_is_better: next })
    } catch (err) {
      // Don't leave the bar asserting a polarity the server rejected.
      setPolarityOverride(null)
      setPolarityError(err instanceof Error ? err.message : 'Request failed')
      console.error('Failed to update spec polarity:', err)
    } finally {
      setPolarityPending(false)
    }
  }, [specId, polarityOverride, serverPolarity])

  const polarityToggle =
    specId != null ? (
      <PolarityToggle
        specName={item.spec_name || item.title}
        higherIsBetter={higherIsBetter}
        pending={polarityPending}
        error={polarityError}
        onToggle={toggleHigherIsBetter}
      />
    ) : null

  // ── Score mode ─────────────────────────────────────────────────
  if (mode === 'score' && scoreValue !== undefined) {
    return (
      <div className="border border-border rounded-lg p-3 bg-card">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-foreground">{item.title}</span>
          <span
            className={`text-sm font-bold ${
              scoreValue >= 80
                ? 'text-success'
                : scoreValue >= 60
                ? 'text-primary'
                : scoreValue >= 40
                ? 'text-warning'
                : 'text-destructive'
            }`}
          >
            {scoreValue}
          </span>
        </div>
        {scoreLabel && <p className="text-xs text-muted-foreground">{scoreLabel}</p>}
      </div>
    )
  }

  // ── Helpers ────────────────────────────────────────────────────

  const handleUpdate = (updates: Partial<ScorecardItemData>) => {
    onUpdate?.(item.id, updates)
  }

  const rawSpecMin = item.spec_typical_min != null ? Number(item.spec_typical_min) || 0 : 0
  const rawSpecMax = item.spec_typical_max != null ? Number(item.spec_typical_max) || 100 : 100

  // Expand range if thresholds fall outside typical min/max (30% padding)
  const lowerT = item.lower_threshold != null ? Number(item.lower_threshold) : null
  const upperT = item.upper_threshold != null ? Number(item.upper_threshold) : null
  const specMin = lowerT != null && lowerT < rawSpecMin
    ? Math.min(rawSpecMin, lowerT * 0.7)
    : rawSpecMin
  const specMax = upperT != null && upperT > rawSpecMax
    ? Math.max(rawSpecMax, upperT * 1.3)
    : rawSpecMax
  const specRange = specMax - specMin || 1

  // ── Importance badge color ─────────────────────────────────────
  const importanceStyle = importanceOptions.find((o) => o.value === item.importance)

  // ── Edit mode (inline) ────────────────────────────────────────

  return (
    <div
      className={`group rounded-lg border transition-colors ${
        mode === 'filter'
          ? 'p-2 border-border bg-card'
          : 'px-4 py-3 border-border/60 bg-card/80 hover:border-primary/30 hover:bg-card'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Drag handle */}
        {isEdit && (
          <GripVertical className="w-4 h-4 text-muted-foreground/20 mt-0.5 cursor-grab flex-shrink-0 group-hover:text-muted-foreground/50 transition-colors" />
        )}

        <div className="flex-1 min-w-0 space-y-1">
          {/* Row 1: Title + importance + delete */}
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              {isEdit ? (
                <InlineTextField
                  value={item.title}
                  onChange={(title) => handleUpdate({ title })}
                  placeholder="Item title..."
                  className="text-sm font-medium text-foreground"
                />
              ) : (
                <span className={`font-medium text-foreground ${mode === 'filter' ? 'text-xs' : 'text-sm'}`}>
                  {item.title}
                </span>
              )}
            </div>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/* Importance dropdown */}
              {isEdit ? (
                <ImportanceDropdown
                  value={item.importance}
                  onChange={(importance) => handleUpdate({ importance })}
                />
              ) : (
                <span
                  className={`text-[10px] font-medium rounded-md px-2 py-0.5 ${importanceStyle?.text || ''}`}
                  style={importanceStyle ? tint(importanceStyle.bgVar, 12) : undefined}
                >
                  {importanceStyle?.label}
                </span>
              )}

              {/* Delete on hover */}
              {isEdit && onDelete && (
                <button
                  onClick={() => onDelete(item.id)}
                  className="p-0.5 text-muted-foreground/0 group-hover:text-muted-foreground/60 hover:!text-destructive transition-colors"
                  title="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Row 2: Description */}
          {(isEdit || item.description) && mode !== 'filter' && (
            <div>
              {isEdit ? (
                <InlineTextField
                  value={item.description}
                  onChange={(description) => handleUpdate({ description })}
                  placeholder="Add a description..."
                  className="text-xs text-muted-foreground leading-relaxed"
                  multiline
                />
              ) : (
                <p className="text-xs text-muted-foreground line-clamp-2">{item.description}</p>
              )}
            </div>
          )}

          {/* Threshold range bar for numeric specs */}
          {isEdit && item.spec_type === 'numeric' && (specMin !== 0 || specMax !== 100 || item.lower_threshold != null) && (
            <ThresholdRangeBar
              min={specMin}
              max={specMax}
              unit={item.spec_unit || ''}
              lowerThreshold={item.lower_threshold ?? specMin + specRange * 0.25}
              upperThreshold={item.upper_threshold ?? specMin + specRange * 0.75}
              higherIsBetter={higherIsBetter}
              onChangeLower={(v) => handleUpdate({ lower_threshold: v })}
              onChangeUpper={(v) => handleUpdate({ upper_threshold: v })}
              action={polarityToggle}
            />
          )}

          {/* Categorical pills */}
          {isEdit && item.spec_type === 'categorical' && item.spec_categories && item.spec_categories.length > 0 && (
            <CategoricalSelector
              categories={item.spec_categories}
              selected={item.acceptable_categories || []}
              onChange={(acceptable_categories) => handleUpdate({ acceptable_categories })}
              multi
            />
          )}

          {/* Ordered categorical — discrete level bar with threshold markers */}
          {isEdit && item.spec_type === 'ordered_categorical' && item.spec_ordered_levels && item.spec_ordered_levels.length > 0 && (
            <OrderedCategoricalBar
              levels={item.spec_ordered_levels}
              minimumAcceptableLevelId={item.minimum_acceptable_level ?? null}
              desirableLevelId={item.desirable_level ?? null}
              higherIsBetter={higherIsBetter}
              onChangeMinAcceptable={(id) => handleUpdate({ minimum_acceptable_level: id })}
              onChangeDesirable={(id) => handleUpdate({ desirable_level: id })}
              action={polarityToggle}
            />
          )}

          {/* Boolean — just show the spec type; observation True = has it, False = doesn't */}
          {isEdit && item.spec_type === 'boolean' && (
            <span className="text-[10px] text-muted-foreground pt-0.5">
              Yes / No capability
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export default ScorecardItemCard
